import { LISTING_STATUS } from '../constants/listingStatus';
import { findListingEntity, findListingVariantEntity } from './entityReadHelpers';

export const getMasterVariantIds = (product, masterVariantIds) => {
  if (Array.isArray(masterVariantIds)) {
    return masterVariantIds;
  }

  return (product?.variants || []).map(({ _id }) => _id);
};

export const getOverwriteVariantIds = ({
  existingDraftVariantIds = [],
  existingLiveVariantIds = [],
  liveOnlyVariantIds = [],
}) => [
  ...new Set([
    ...existingDraftVariantIds,
    ...existingLiveVariantIds,
    ...liveOnlyVariantIds,
  ]),
];

export const getVariantChannelEntry = (source, masterVariantId, channelId) => {
  if (!masterVariantId || !channelId) return null;

  if (source?.entities?.listingVariants) {
    return findListingVariantEntity(source, masterVariantId, channelId);
  }

  return source?.variants
    ?.find(({ _id }) => _id === masterVariantId)
    ?.channels
    ?.find((channel) => channel.salesChannelId === channelId
      || channel.name === channelId
      || String(channel.salesChannelId) === String(channelId)) ?? null;
};

export const isVariantChannelDraft = (variantChannelEntry) => (
  variantChannelEntry?.listingStatus === LISTING_STATUS.DRAFT
);

export const isVariantChannelLive = (variantChannelEntry) => Boolean(variantChannelEntry?.live);

export const productHasAnyLiveVariant = (product, productListings = {}, productsState = null) => {
  const hasLiveInListings = Object.values(productListings).some(
    (listing) => (listing?.listingProduct?.live?.variants || []).length > 0,
  );

  if (hasLiveInListings) return true;

  if (productsState?.entities?.listings) {
    const hasLiveListingVariants = Object.values(productsState.entities.listings).some(
      (listing) => (listing?.live?.variants || []).length > 0,
    );

    if (hasLiveListingVariants) return true;
  }

  if (productsState?.entities?.listingVariants) {
    if (Object.values(productsState.entities.listingVariants).some(isVariantChannelLive)) {
      return true;
    }
  }

  return (product?.variants || []).some(
    (variant) => (variant.channels || []).some(isVariantChannelLive),
  );
};

const resolveListingForChannel = (product, productsState, channelId) => {
  if (product?._id && productsState) {
    const listing = findListingEntity(productsState, { masterProductId: product._id, channelId });
    if (listing) return listing;
  }

  return product?.channels
    ?.find((channel) => channel.salesChannelId === channelId
      || channel.name === channelId
      || String(channel.salesChannelId) === String(channelId))
    ?.listingProduct ?? null;
};

const resolveVariantChannelEntry = (product, productsState, masterVariantId, channelId) => {
  if (productsState?.entities?.listingVariants) {
    return findListingVariantEntity(productsState, masterVariantId, channelId);
  }

  return getVariantChannelEntry(product, masterVariantId, channelId);
};

export const getDraftSyncGroups = ({ product, productsState, channelIds, masterVariantIds }) => (
  channelIds.reduce((groups, channelId) => {
    const listingProduct = resolveListingForChannel(product, productsState, channelId);

    if (!listingProduct) {
      groups.channelsWithoutListing.push(channelId);
      return groups;
    }

    const selectedVariantIds = getMasterVariantIds(product, masterVariantIds);
    const existingDraftVariantIds = [];
    const existingLiveVariantIds = [];
    const missingVariantIds = [];
    const draftListingMissingVariantIds = [];
    const draftListingLiveOnlyVariantIds = [];

    selectedVariantIds.forEach((masterVariantId) => {
      const entry = resolveVariantChannelEntry(product, productsState, masterVariantId, channelId);

      if (isVariantChannelDraft(entry)) {
        existingDraftVariantIds.push(masterVariantId);
        return;
      }

      if (isVariantChannelLive(entry)) {
        if (listingProduct.listingStatus === LISTING_STATUS.PUBLISHED) {
          existingLiveVariantIds.push(masterVariantId);
        } else {
          draftListingLiveOnlyVariantIds.push(masterVariantId);
        }
        return;
      }

      if (entry) return;

      if (listingProduct.listingStatus === LISTING_STATUS.PUBLISHED) {
        missingVariantIds.push(masterVariantId);
      } else {
        draftListingMissingVariantIds.push(masterVariantId);
      }
    });

    if (listingProduct.listingStatus === LISTING_STATUS.PUBLISHED) {
      groups.publishedListingChannels.push({
        channelId,
        existingDraftVariantIds,
        existingLiveVariantIds,
        missingVariantIds,
      });
      return groups;
    }

    groups.draftListingChannels.push({
      channelId,
      existingDraftVariantIds,
      missingVariantIds: draftListingMissingVariantIds,
      liveOnlyVariantIds: draftListingLiveOnlyVariantIds,
    });

    return groups;
  }, {
    channelsWithoutListing: [],
    publishedListingChannels: [],
    draftListingChannels: [],
  })
);
